import { Country, StockClassType } from '@prisma/client';
import { z } from 'zod';
import { BeneficialOwnerControlType, ShareholdingType } from './types';

const controlTypeEnum = z.enum([
  BeneficialOwnerControlType.DIRECT_OWNERSHIP,
  BeneficialOwnerControlType.INDIRECT_OWNERSHIP,
  BeneficialOwnerControlType.DIFFERENTIAL_VOTING_RIGHTS,
  BeneficialOwnerControlType.APPOINT_SENIOR_MANAGEMENT,
  BeneficialOwnerControlType.DEBT_INSTRUMENTS,
  BeneficialOwnerControlType.POSITIONS_WITHIN_LEGAL_PERSON,
  BeneficialOwnerControlType.ANOTHER_PERSON,
  BeneficialOwnerControlType.OTHER,
]);

const pathSchema = z
  .object({
    sourceCorporationId: z.number(),
    sourceCorporationName: z.string().nullable(),
    controlType: controlTypeEnum,
  })
  .passthrough();

const shareholdingSchema = z
  .object({
    stakeholdingId: z.string(),
    name: z.string(),
    type: z.enum([ShareholdingType.INDIVIDUAL, ShareholdingType.INSTITUTION]),
    ownershipPercent: z.number().nullable(),
    dilutedOwnershipPercent: z.number().nullable(),
    hasSignificantControl: z.boolean(),
    stockClass: z
      .object({
        id: z.string(),
        name: z.string(),
        type: z.nativeEnum(StockClassType),
      })
      .nullable(),
  })
  .passthrough();

const beneficialOwnerSchema = z
  .object({
    id: z.string(),
    directorId: z.number(),
    name: z.string(),
    directControlType: controlTypeEnum.nullable(),
    paths: z.array(pathSchema),
  })
  .passthrough();

const recentDocumentSchema = z
  .object({
    id: z.number(),
    fileName: z.string(),
    fileUrl: z.string(),
    extension: z.string().nullable(),
    uploadedAt: z.string(),
  })
  .passthrough();

export const ownershipResponseSchema = z
  .object({
    entity: z
      .object({
        id: z.number(),
        uboThresholdPercent: z.number(),
        companyId: z.number(),
      })
      .passthrough(),
    percentScale: z.number().positive(),
    totalOwnershipPercent: z.number(),
    shareholdings: z.array(shareholdingSchema),
    pagination: z
      .object({
        offset: z.number(),
        limit: z.number(),
        total: z.number(),
      })
      .passthrough(),
    beneficialOwners: z.array(beneficialOwnerSchema),
    recentDocuments: z.array(recentDocumentSchema),
  })
  .passthrough();

export const inheritableUboListSchema = z.array(
  z
    .object({
      directorId: z.number(),
      name: z.string(),
      nationality: z.nativeEnum(Country).nullable(),
    })
    .passthrough(),
);

export const directorDeletePreviewSchema = z
  .object({
    keyPersonOf: z.array(
      z
        .object({
          corporationId: z.number(),
          corporationName: z.string(),
        })
        .passthrough(),
    ),
    uboOf: z.array(
      z
        .object({
          corporationId: z.number(),
          corporationName: z.string(),
        })
        .passthrough(),
    ),
    cascadeRemoves: z.array(
      z
        .object({
          corporationId: z.number(),
          corporationName: z.string(),
          through: z.array(
            z
              .object({
                corporationId: z.number(),
                corporationName: z.string(),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
    ),
  })
  .passthrough();
